import { ComponentPropsWithoutRef, ElementType, MouseEventHandler, ReactNode } from 'react';
import { styled, lighten } from '@mui/material';
import { Box } from '@o2/ui';
import colors from '@o2/ui/theme/colors';
import { shouldForwardProp as sysShouldForwardProp } from '@mui/system';

export type ButtonProps<T extends ElementType = 'button'> = {
  onClick?: MouseEventHandler;
  /**
   * @deprecated Use `children` instead.
   */
  title?: ReactNode;
  icon?: ReactNode;
  active?: boolean;

  children?: ReactNode;
  /**
   * What component to render this component as (inheriting props).
   * Useful when combining with e.g. react-router.
   */
  as?: T;
  /**
   * Internal prop changes color of the item.
   */
  internal?: boolean;
} & ComponentPropsWithoutRef<T>;

const StyledButton = styled('button', {
  name: 'SidebarMenu',
  slot: 'button',
  shouldForwardProp: (propName) => sysShouldForwardProp(propName) && propName !== 'active',
})<Omit<ButtonProps, 'title' | 'icon'>>(({ theme, internal }) => ({
  display: 'flex',
  flexDirection: 'row',
  textDecoration: 'none',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(2, 4),
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  color: theme.palette.primary.main,
  fontFamily: 'inherit',
  fontWeight: theme.typography.fontWeightRegular,
  transition: 'background 100ms ease-out, color 100ms ease-out',
  backgroundColor: theme.palette.background.white,
  '&:hover': {
    textDecoration: 'underline',
    textUnderlineOffset: '0.3em',
  },
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'row',
  },
  ...(internal && {
    backgroundColor: colors.internal[100],
    '&:hover': {
      backgroundColor: colors.brandPrimary[100],
      textDecoration: 'underline',
      textUnderlineOffset: '0.3em',
    },
  }),
}));

const Button = <T extends ElementType = 'button'>({ children, title, icon, ...other }: ButtonProps<T>) => {
  return (
    <StyledButton {...other}>
      {icon && <Box>{icon}</Box>}
      <Box>{children ?? title}</Box>
    </StyledButton>
  );
};

Button.displayName = 'SidebarMenu.Button';

export default Button;
